import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Separator } from "@/components/ui/separator";
import { AlertCircle } from "lucide-react";
import PreOrderModal from "@/components/PreOrderModal";
const MEAT_IMAGE_1 = "/images/lamb.jpeg";
const MEAT_IMAGE_2 = "/images/beef.jpeg";
const MEAT_IMAGE_3 = "/images/chicken.jpeg";
const MEAT_IMAGE_4 = "/images/shashlik.jpeg";
// At top of Products.js add German names mapping
const GERMAN_NAMES = {
  lammkeule: "Lammkeule",
  lammknacken: "Lammknacken",
  lammschulter: "Lammschulter",
  lammrippen: "Lammrippen",
  lammkotelett: "Lammkotelett",
  lammleber: "Lammleber",
  lammhackfleisch: "Lammhackfleisch",
  rinderrippen: "Rinderrippen",
  rinderklugen: "Rinderklugen",
  rinderhack: "Rinderhack (ohne Gewürz)",
  unterkeule: "Unterkeule",
  kalbskugel: "Kalbskugel",
  haehnchenbrust: "Hähnchenbrust",
  haehnchenkeule: "Hähnchenkeule",
  haehnchenleber: "Hähnchenleber",
  haehnchenmagen: "Hähnchenmagen",
  haehnchenfluegel: "Hähnchenflügel",
  haehnchenherz: "Hähnchenherz",
  putenfleisch: "Putenfleisch",
  schaschlik: "Schaschlik",
};
const categories = [
  {
    key: "lamb",
    items: [
      { key: "lammkeule" },
      { key: "lammknacken" },
      { key: "lammschulter" },
      { key: "lammrippen" },
      { key: "lammkotelett" },
      { key: "lammleber" },
      { key: "lammhackfleisch" },
    ],
  },
  {
    key: "beef",
    items: [
      { key: "rinderrippen" },
      { key: "rinderklugen" },
      { key: "rinderhack" },
      { key: "unterkeule" },
      { key: "kalbskugel" },
    ],
  },
  {
    key: "chicken",
    items: [
      { key: "haehnchenbrust" },
      { key: "haehnchenkeule" },
      { key: "haehnchenleber" },
      { key: "haehnchenmagen" },
      { key: "haehnchenfluegel" },
      { key: "haehnchenherz" },
    ],
  },
  {
    key: "special",
    items: [
      { key: "putenfleisch" },
      { key: "schaschlik" },
    ],
  },
];

const turkishProducts = [
  { key: "dairy",         image: "/images/cheese.jpg" },
  { key: "olivesPickles", image: "/images/olives.jpeg" },
  { key: "spicesHerbs",   image: "/images/spicescat.jpg" },
  { key: "riceGrains",    image: "/images/rice.jpg" },
  { key: "breadFrozen",   image: "/images/bread.jpg" },
  { key: "snacksSweets",  image: "/images/chips.jpg" },
  { key: "fruitVeg",      image: "/images/veg.jpg" },
];

export default function Products() {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState("chicken");
  const [preOrderProduct, setPreOrderProduct] = useState(null);
   

  return (
    <section id="products" data-testid="products-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2
            data-testid="products-title"
            className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight mb-4"
            style={{ color: "#0A0A0A" }}
          >
            {t("products.title")}
          </h2>
          <div className="w-16 h-0.5 mx-auto mb-4" style={{ backgroundColor: "#D4AF37" }} />
          <p className="text-base text-neutral-500">{t("products.subtitle")}</p>
        </div>

        {/* Decorative meat images - one per category */}
<div className="flex justify-center gap-4 mb-12">
  <img
    src={MEAT_IMAGE_1}
    alt="Lamb"
    className="w-44 h-28 object-cover rounded-sm shadow-md hidden sm:block"
  />
  <img
    src={MEAT_IMAGE_2}
    alt="Beef"
    className="w-44 h-28 object-cover rounded-sm shadow-md hidden sm:block"
  />
  <img
    src={MEAT_IMAGE_3}
    alt="Chicken"
    className="w-44 h-28 object-cover rounded-sm shadow-md hidden sm:block"
  />
  <img
    src={MEAT_IMAGE_4}
    alt="Special"
    className="w-44 h-28 object-cover rounded-sm shadow-md hidden sm:block"
  />
</div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              data-testid={`category-tab-${cat.key}`}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-6 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                activeCategory === cat.key
                  ? "text-white shadow-md"
                  : "text-neutral-600 bg-neutral-100 hover:bg-neutral-200"
              }`}
              style={
                activeCategory === cat.key
                  ? { backgroundColor: "#8B0000" }
                  : {}
              }
            >
              {t(`products.${cat.key}`)}
            </button>
          ))}
        </div>

       {/* Product List */}
{categories
  .filter((cat) => cat.key === activeCategory)
  .map((cat) => (
    <div key={cat.key} className="max-w-2xl mx-auto mb-8" data-testid={`product-table-${cat.key}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
  {cat.items.map((item, index) => (
    <div
      key={item.key}
      className="flex items-center justify-between gap-4 px-5 py-4 bg-[#F9F9F9] rounded-sm border border-neutral-100 hover:border-[#D4AF37] transition-colors"
    >
      <div className="flex items-center gap-4">
        <span className="text-xs font-mono text-neutral-400 w-5 shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="text-sm font-medium" style={{ color: "#0A0A0A" }}>
          {t(`products.items.${item.key}`)}
        </span>
      </div>
      <button
        onClick={() => setPreOrderProduct({
  display: t(`products.items.${item.key}`),
  de: GERMAN_NAMES[item.key] || t(`products.items.${item.key}`)
})}
        className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-sm text-white transition-opacity hover:opacity-80"
        style={{ backgroundColor: "#8B0000" }}
      >
        {t("preorder.button")}
      </button>
    </div>
  ))}
</div>
    </div>
  ))}

        {/* Disclaimer */}
        <div className="max-w-2xl mx-auto mb-20">
          <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-sm border border-neutral-100">
            <AlertCircle className="h-5 w-5 text-neutral-400 mt-0.5 shrink-0" />
            <p className="text-sm text-neutral-500 leading-relaxed">
              {t("products.disclaimer")}
            </p>
          </div>
        </div>

        <Separator className="mb-20" />

        {/* Turkish Products Section */}
        <div className="text-center mb-12">
          <h3
  data-testid="products-more-title"
  className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight mb-4"
  style={{ color: "#8B0000" }}
>
  {t("products.moreTitle")}
</h3>
<p className="text-base text-neutral-500">{t("products.moreSubtitle")}</p>
        </div>

        <div className="max-w-5xl mx-auto">
  {/* First row - 4 items */}
<div className="flex justify-center gap-12 mb-16 flex-wrap">
  {turkishProducts.slice(0, 4).map((product) => (
    <div
      key={product.key}
      data-testid={`turkish-product-${product.key}`}
      className="flex flex-col items-center group"
    >
      <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden border-4 border-neutral-100 shadow-md group-hover:shadow-lg transition-shadow mb-3">
        <img
          src={product.image}
          alt={t(`products.${product.key}`)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <span className="text-sm font-medium text-neutral-700 text-center max-w-[120px]">
        {t(`products.${product.key}`)}
      </span>
    </div>
  ))}
</div>

{/* Second row - 3 items centered */}
<div className="flex justify-center gap-12 flex-wrap">
  {turkishProducts.slice(4).map((product) => (
    <div
      key={product.key}
      data-testid={`turkish-product-${product.key}`}
      className="flex flex-col items-center group"
    >
      <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden border-4 border-neutral-100 shadow-md group-hover:shadow-lg transition-shadow mb-3">
        <img
          src={product.image}
          alt={t(`products.${product.key}`)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <span className="text-sm font-medium text-neutral-700 text-center max-w-[120px]">
        {t(`products.${product.key}`)}
      </span>
    </div>
  ))}
</div>
</div>
      </div>
       {preOrderProduct && (
  <PreOrderModal
    product={preOrderProduct.display}
    productDE={preOrderProduct.de}
    onClose={() => setPreOrderProduct(null)}
  />
)}

    </section>
  );
}
